import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Globe, Video, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const Therapists = () => {
  const [therapists, setTherapists] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [booking, setBooking] = useState(null);
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");

  const loadAppts = async () => { const { data } = await api.get("/appointments"); setAppointments(data); };
  useEffect(() => {
    api.get("/therapists").then(({ data }) => setTherapists(data)).catch(() => {});
    loadAppts();
  }, []);

  const book = async () => {
    if (!date) { toast.info("Please choose a preferred date."); return; }
    try {
      await api.post("/appointments", { therapist_id: booking.id, preferred_date: date, notes });
      setBooking(null); setDate(""); setNotes("");
      await loadAppts();
      toast.success("Appointment request sent. The clinic will be in touch.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="therapists-page">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink">Find a Therapist</h1>
        <p className="text-ink-2 mt-2">Culturally attuned, real-world professionals. Those near you appear first.</p>
      </div>

      {appointments.length > 0 && (
        <div className="bg-calm rounded-3xl p-6">
          <h3 className="font-serif text-2xl text-calm-text mb-3">Your requests</h3>
          <div className="space-y-2">
            {appointments.map((a) => (
              <div key={a.id} className="flex items-center gap-3 bg-linen/70 rounded-2xl px-4 py-3" data-testid="appointment-item">
                <CalendarCheck className="w-5 h-5 text-brand" />
                <div className="flex-1">
                  <p className="text-sm text-calm-text font-medium">{a.therapist_name}</p>
                  <p className="text-xs text-calm-text/70">{a.preferred_date} · {a.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-6">
        {therapists.map((t) => (
          <div key={t.id} className="bg-white rounded-3xl border border-linen-3 p-6" data-testid="therapist-card">
            <div className="flex gap-4">
              <img src={t.photo} alt={t.name} className="w-16 h-16 rounded-2xl object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-serif text-2xl text-ink">{t.name}</h3>
                  {t.local && <span className="text-xs bg-calm text-calm-text px-2 py-0.5 rounded-full">Near you</span>}
                </div>
                <p className="text-sm text-ink-2">{t.title}</p>
                <p className="text-xs text-ink-mute flex items-center gap-1.5 mt-1"><MapPin className="w-3.5 h-3.5" />{t.city}, {t.country}{t.telehealth && <><Video className="w-3.5 h-3.5 ml-2" /> Telehealth</>}</p>
              </div>
            </div>
            <p className="text-sm text-ink-2 mt-3 flex items-start gap-1.5"><Globe className="w-4 h-4 mt-0.5 shrink-0 text-accent" />{t.cultural_focus}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {[...t.specialties, ...t.modalities].slice(0, 5).map((s, i) => (
                <span key={i} className="text-xs bg-linen-2 text-ink-2 px-2.5 py-1 rounded-full">{s}</span>
              ))}
            </div>
            <p className="text-xs text-ink-mute mt-2">Languages: {t.languages.join(", ")}</p>
            <Button onClick={() => setBooking(t)} className="rounded-full bg-brand hover:bg-brand-hover text-linen mt-4 w-full" data-testid="book-button">Request appointment</Button>
          </div>
        ))}
      </div>

      <Dialog open={!!booking} onOpenChange={(o) => !o && setBooking(null)}>
        <DialogContent className="bg-linen rounded-3xl" data-testid="booking-dialog">
          <DialogHeader><DialogTitle className="font-serif text-3xl text-ink">Request with {booking?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><label className="text-sm text-ink-2">Preferred date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-2xl bg-white border-linen-3 h-12" data-testid="booking-date" /></div>
            <div className="space-y-2"><label className="text-sm text-ink-2">Anything to share (optional)</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-2xl bg-white border-linen-3" data-testid="booking-notes" /></div>
            <Button onClick={book} className="rounded-full bg-brand hover:bg-brand-hover text-linen w-full h-12" data-testid="booking-confirm">Send request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Therapists;
